import { motion } from 'framer-motion';
import React from 'react';

type DatasetProps = {
  id: string;
  label: string;
  data: {
    value: number;
    suffix?: string;
    color: string;
  }[];
};

type PieChartProps = {
  dataset: DatasetProps[];
};

const getConicGradient = (data: DatasetProps[], totalValue: number) => {
  const gradient: string[] = [];
  let cumulativePercent = 0;

  data.forEach((item) => {
    const { color, value } = item.data[0];
    const percentage = Math.floor(
      totalValue > 0 ? (value / totalValue) * 100 : 0,
    );
    const nextCumulativePercent = cumulativePercent + percentage;
    gradient.push(`${color} ${cumulativePercent}% ${nextCumulativePercent}%`);
    cumulativePercent = nextCumulativePercent;
  });

  return `conic-gradient(${gradient.join(', ')})`;
};

export const PieChart: React.FC<PieChartProps> = ({ dataset }) => {
  return (
    <div className="flex w-full flex-col space-y-6">
      <motion.div
        className="aspect-[1/1] w-full rounded-full"
        initial={{
          opacity: 1,
          background: getConicGradient(dataset, 0),
        }}
        transition={{
          ease: 'easeOut',
          duration: 0.4,
        }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          background: getConicGradient(dataset, 100),
        }}
      />

      <div className="flex w-full flex-col divide-y-2 divide-neutral-4/20">
        {dataset.map(({ data, id, label }) => (
          <div key={id} className="flex space-x-3 py-3 first:pt-0 last:pb-0">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <span
                className="h-4 w-4 rounded-full"
                style={{ 'backgroundColor': data[0].color }}
              />
            </span>
            <span className="flex-1">{label}</span>
            <span className="shrink-0">
              {data[0].value}
              {data[0].suffix || null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
